import React, { FC } from 'react';
import { VideoType } from 'phoenix2/state/video/video';
import { useVideoUrls } from 'phoenix2/components/widgets/video-player/hooks/use-video-urls';
import { useVideoLoadState } from 'phoenix2/components/widgets/video-player/hooks/use-video-load-state';
import { usePosterLoadState } from 'phoenix2/components/widgets/video-player/hooks/use-poster-load-state';
import VideoPlayer from 'phoenix2/components/widgets/video-player/video-player';
import { useVideoCallbacks } from 'phoenix2/components/widgets/video-player/hooks/use-video-callbacks';

interface IVideoPlayerContainerProps {
    /**
     * The index of the activity
     */
    activityIndex?: number;

    /**
     * Controls whether we send credentials on video load
     */
    corsSafe?: boolean;

    /**
     * True when the component must ensure the video is paused
     * and not in full screen mode
     */
    forcePause?: boolean;

    /**
     * If true, the video automatically begins playing.
     */
    autoPlay?: boolean;

    /**
     * If true the user can't fast forward the video.
     */
    disableSeek?: boolean;

    /**
     * If true, the video poster is not displayed regardless if it there
     * is a poster url present.
     */
    hidePoster?: boolean;

    /**
     * Set to true if the video is not associated with a particular skill
     */
    isTutorialVideo?: boolean;

    /**
     * Callback when the video reaches the end
     */
    onReachedEnd?: () => void;

    /**
     * If true, show subtitles for the video
     */
    showSubtitles?: boolean;

    /**
     * The ID of the skill?
     */
    skillId?: string;

    /**
     * If true, skip the video to the final frame
     * (used in tethering where no poster is available)
     */
    skipToFinalFrame?: boolean;

    /**
     * Custom styling for the video player
     */
    style?: Record<string, string>;

    /**
     * The id of the video
     */
    videoId: string;

    /**
     * Callback function for when the video fails to load
     */
    videoLoadFail?: () => void;

    /**
     * Can be "skill" (i.e. a video about a skill), or "tutorial" (a
     * video for teaching how to use Sparx)
     */
    videoType: VideoType;

    /**
     * Additional key-value pairs to be included in analytics events
     */
    additionalMetadata?: { [key: string]: string };

    forceUserToWatch?: boolean;

    /**
     * If specified, the `onReachedEnd` callback is triggered this many
     * seconds before the end of the video - otherwise, it is 0.02 seconds before
     */
    outroLength?: number;
}

export const VideoPlayerContainer: FC<IVideoPlayerContainerProps> = ({
    activityIndex,
    corsSafe = false,
    forcePause = false,
    disableSeek = false,
    autoPlay = false,
    hidePoster = false,
    isTutorialVideo = false,
    onReachedEnd = () => {
        /* noop */
    },
    showSubtitles = false,
    skillId,
    skipToFinalFrame = false,
    style = {},
    videoId,
    videoLoadFail,
    videoType,
    additionalMetadata,
    outroLength,
}) => {
    const { posterUrl, subtitleUrl, videoUrl } = useVideoUrls(videoId, videoType);
    const { errorLoadingVideo, videoLoadAttempt, videoLoaded } = useVideoLoadState(
        videoId,
        videoType,
    );
    const { errorLoadingPoster, posterLoadAttempt, posterLoaded } = usePosterLoadState(
        videoId,
        videoType,
    );

    const {
        onEnd,
        onFullscreen,
        onPause,
        onPlay,
        onPosterLoadEnd,
        onPosterLoadFail,
        onPosterLoadStart,
        onPosterLoadSuccess,
        onReplay,
        onSeeked,
        onStart,
        onVideoCanPlay,
        onVideoErrorClicked,
        onVideoLoadEnd,
        onVideoLoadFail,
        onVideoLoadStart,
        onVideoLoadSuccess,
    } = useVideoCallbacks(
        videoId,
        videoType,
        videoUrl,
        posterUrl,
        skillId,
        activityIndex,
        videoLoadFail,
        additionalMetadata,
    );

    return (
        <VideoPlayer
            corsSafe={corsSafe}
            errorLoadingPoster={errorLoadingPoster}
            errorLoadingVideo={errorLoadingVideo}
            forcePause={forcePause}
            disableSeek={disableSeek}
            autoPlay={autoPlay}
            hidePoster={hidePoster}
            isTutorialVideo={isTutorialVideo}
            outroLength={outroLength}
            onEnd={onEnd}
            onFullscreen={onFullscreen}
            onPause={onPause}
            onPlay={onPlay}
            onPosterLoadEnd={onPosterLoadEnd}
            onPosterLoadFail={onPosterLoadFail}
            onPosterLoadStart={onPosterLoadStart}
            onPosterLoadSuccess={onPosterLoadSuccess}
            onReachedEnd={onReachedEnd}
            onReplay={onReplay}
            onSeeked={onSeeked}
            onStart={onStart}
            onVideoCanPlay={onVideoCanPlay}
            onVideoErrorClicked={onVideoErrorClicked}
            onVideoLoadEnd={onVideoLoadEnd}
            onVideoLoadFail={onVideoLoadFail}
            onVideoLoadStart={onVideoLoadStart}
            onVideoLoadSuccess={onVideoLoadSuccess}
            posterLoaded={posterLoaded}
            posterLoadAttempt={posterLoadAttempt}
            posterUrl={posterUrl}
            showSubtitles={showSubtitles}
            skipToFinalFrame={skipToFinalFrame}
            style={style}
            subtitleUrl={subtitleUrl}
            videoId={videoId}
            videoLoaded={videoLoaded}
            videoLoadAttempt={videoLoadAttempt}
            videoType={videoType}
            videoUrl={videoUrl}
        />
    );
};

export default VideoPlayerContainer;
